import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';

// Textarea that grows to fit its content so narration text is never clipped
// and the whole modal body scrolls as one (no confusing nested scrollbars).
const AutoGrowTextarea = ({ value, onChange }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={3}
      className="w-full px-4 py-3 text-sm text-gray-700 resize-none overflow-hidden min-h-[72px] focus:outline-none focus:ring-2 focus:ring-indigo-500/20 border-0"
    />
  );
};

const NarrationEditorModal = ({ narrations, narrationsStale, onSave, onGenerate, onClose, saving, generating }) => {
  const [localNarrations, setLocalNarrations] = useState([]);

  useEffect(() => {
    setLocalNarrations(narrations.map(n => ({ ...n })));
  }, [narrations]);

  const handleTextChange = (index, text) => {
    setLocalNarrations(prev => prev.map((n, i) => i === index ? { ...n, narration_text: text } : n));
  };

  const handleSave = () => {
    onSave(localNarrations);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
              <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Presentation Narration</h2>
              <p className="text-sm text-gray-500">Edit what the presenter says during the guided presentation</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Stale warning */}
        {narrationsStale && (
          <div className="mx-6 mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-sm text-amber-800">
              Proposal content has changed. Narrations may be outdated.{' '}
              <button onClick={onGenerate} disabled={generating} className="font-semibold underline hover:no-underline">
                Regenerate
              </button>
            </p>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {localNarrations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <h3 className="text-base font-medium text-gray-900 mb-1">No narration generated yet</h3>
              <p className="text-sm text-gray-500 mb-6">Generate narration scripts from the proposal sections for the presenter.</p>
              <button
                onClick={onGenerate}
                disabled={generating}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {generating && <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"></span>}
                Generate Narration
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {localNarrations.map((narration, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {narration.section_title}
                    </span>
                  </div>
                  <AutoGrowTextarea
                    value={narration.narration_text}
                    onChange={(e) => handleTextChange(index, e.target.value)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {localNarrations.length > 0 && (
          <div className="flex flex-col gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <button
              onClick={onGenerate}
              disabled={generating}
              className="order-last inline-flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors sm:order-none sm:w-auto"
            >
              {generating && <span className="animate-spin w-4 h-4 border-2 border-gray-400/30 border-t-gray-600 rounded-full"></span>}
              Regenerate All
            </button>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <button
                onClick={onClose}
                className="order-2 w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors sm:order-none sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="order-1 inline-flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors sm:order-none sm:w-auto"
              >
                {saving && <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"></span>}
                Save Narration
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NarrationEditorModal;
